/**
 * Uvoz noćnih sati iz Glovo/Wolt izvještaja (Excel): retci s "Night" u stupcu smjene,
 * zbroj "Efektivni rad" po ID-u koji se mapira na glovoId / woltId korisnika.
 */

export function parseDateCell(raw) {
  if (raw == null || raw === '') return null;
  if (raw instanceof Date && !Number.isNaN(raw.getTime())) return raw;
  if (typeof raw === 'number') {
    const utcDays = Math.floor(raw - 25569);
    const utcValue = utcDays * 86400;
    const dateInfo = new Date(utcValue * 1000);
    return Number.isNaN(dateInfo.getTime()) ? null : dateInfo;
  }
  const str = String(raw).trim();
  if (!str) return null;

  const m = str.match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{2,4})$/);
  if (m) {
    const day = parseInt(m[1], 10);
    const month = parseInt(m[2], 10);
    let year = parseInt(m[3], 10);
    if (year < 100) year += 2000;
    const d = new Date(year, month - 1, day);
    if (
      d.getFullYear() === year &&
      d.getMonth() === month - 1 &&
      d.getDate() === day
    ) {
      return d;
    }
    return null;
  }

  const parsed = new Date(str);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function normalizeHeader(h) {
  return String(h ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
}

/** Jedinstveni ID iz Excela (broj ili tekst) */
export function normalizeExternalId(raw) {
  if (raw == null || raw === '') return '';
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    if (Number.isInteger(raw)) return String(raw);
    return String(Math.trunc(raw) === raw ? Math.trunc(raw) : raw);
  }
  return String(raw).trim();
}

export function parseEffectiveHoursCell(raw) {
  if (raw == null || raw === '') return 0;
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return Math.max(0, raw);
  }
  const s = String(raw).trim().replace(',', '.');
  const n = parseFloat(s);
  if (Number.isNaN(n) || n < 0) return 0;
  return n;
}

function isNightShift(cell) {
  const s = String(cell ?? '')
    .trim()
    .toLowerCase();
  return s === 'night' || s === 'noć' || s === 'noc';
}

/**
 * @param {Array<{ id: string, glovoId?: string, woltId?: string }>} people
 * @returns {Map<string, string>} normalizirani vanjski ID -> userId
 */
export function buildExternalIdToUserMap(people) {
  const map = new Map();
  for (const p of people) {
    const gid = normalizeExternalId(p.glovoId);
    if (gid) map.set(gid, p.id);
    const wid = normalizeExternalId(p.woltId);
    if (wid) map.set(wid, p.id);
  }
  return map;
}

/**
 * Pronađi indekse stupaca (0-based) iz retka zaglavlja.
 */
function findColumnIndices(headerRow) {
  let datumCol = -1;
  let idCol = -1;
  let shiftCol = -1;
  let effCol = -1;

  headerRow.eachCell({ includeEmpty: false }, (cell, colNumber) => {
    const norm = normalizeHeader(cell.text ?? cell.value);
    if (!norm) return;

    if (datumCol < 0 && norm === 'datum') datumCol = colNumber - 1;
    if (idCol < 0) {
      if (norm === 'id broj' || norm.includes('id broj')) idCol = colNumber - 1;
      else if (norm === 'id') idCol = colNumber - 1;
    }
    if (shiftCol < 0) {
      if (norm.includes('vrijeme') && norm.includes('smjene')) {
        shiftCol = colNumber - 1;
      }
    }
    if (effCol < 0) {
      if (norm.includes('efektivni') && norm.includes('rad')) {
        effCol = colNumber - 1;
      }
    }
  });

  return { datumCol, idCol, shiftCol, effCol };
}

/**
 * @param {import('exceljs').Worksheet} worksheet
 * @param {object} opts
 * @param {number} opts.year
 * @param {number} opts.month 1-12
 * @param {Map<string, string>} opts.externalIdToUserId
 */
export function aggregateNightHoursFromSheet(worksheet, opts) {
  const { year, month, externalIdToUserId } = opts;
  const totals = new Map();
  const unknownIds = new Set();
  let nightRowsInMonth = 0;
  let skippedNotNight = 0;
  let skippedBadDate = 0;
  let skippedOutsideMonth = 0;

  let headerRowIndex = -1;
  let cols = null;
  const rows = worksheet.rowCount || 0;

  for (let r = 1; r <= Math.min(rows, 50); r++) {
    const row = worksheet.getRow(r);
    const idx = findColumnIndices(row);
    if (idx.datumCol >= 0 && idx.idCol >= 0 && idx.shiftCol >= 0 && idx.effCol >= 0) {
      headerRowIndex = r;
      cols = idx;
      break;
    }
  }

  if (!cols) {
    return {
      ok: false,
      error:
        'Nije pronađeno zaglavlje (potrebni stupci: Datum, ID broj, Vrijeme smjene, Efektivni rad).',
      totals,
      unknownIds: [],
      nightRowsInMonth: 0,
      skippedNotNight: 0,
      skippedBadDate: 0,
      skippedOutsideMonth: 0,
    };
  }

  for (let r = headerRowIndex + 1; r <= rows; r++) {
    const row = worksheet.getRow(r);
    const dateRaw = row.getCell(cols.datumCol + 1).value;
    const idRaw = row.getCell(cols.idCol + 1).value;
    const shiftRaw = row.getCell(cols.shiftCol + 1).value;
    const effRaw = row.getCell(cols.effCol + 1).value;

    const extId = normalizeExternalId(idRaw);
    if (!extId) continue;

    if (!isNightShift(shiftRaw)) {
      skippedNotNight += 1;
      continue;
    }

    const d = parseDateCell(dateRaw);
    if (!d) {
      skippedBadDate += 1;
      continue;
    }
    if (d.getFullYear() !== year || d.getMonth() + 1 !== month) {
      skippedOutsideMonth += 1;
      continue;
    }

    nightRowsInMonth += 1;
    const userId = externalIdToUserId.get(extId);
    if (!userId) {
      unknownIds.add(extId);
      continue;
    }

    const h = parseEffectiveHoursCell(effRaw);
    totals.set(userId, (totals.get(userId) || 0) + h);
  }

  return {
    ok: true,
    totals,
    unknownIds: [...unknownIds],
    nightRowsInMonth,
    skippedNotNight,
    skippedBadDate,
    skippedOutsideMonth,
  };
}
